<template>
    <div class="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-black/5">
        <div class="flex items-center justify-between gap-3 border-b border-gray-300 px-6 py-3">
            <div class="flex min-w-0 items-center gap-2.5">
                <span class="relative flex size-2.5">
                    <span class="absolute inline-flex size-full animate-ping rounded-full bg-green-400 opacity-60" />
                    <span class="relative inline-flex size-2.5 rounded-full bg-green-500" />
                </span>
                <h3 class="text-lg font-bold text-gray-700">Live preview</h3>
            </div>
            <div class="flex items-center gap-2">
                <UIcon v-if="loading && loaded" name="i-ph-circle-notch-bold" class="size-4 animate-spin text-gray-400" />
                <UBadge v-if="loaded && !failed" color="gray" variant="subtle" class="tabular-nums">
                    {{ slots.length }} {{ slots.length === 1 ? 'slot' : 'slots' }}
                </UBadge>
            </div>
        </div>

        <p class="border-b border-dashed border-gray-200 px-6 py-2.5 text-xs text-gray-500">
            The times patients can book in the SAMU app over the next {{ days }} days. Slots that start too soon to book are hidden.
        </p>

        <div v-if="!loaded" class="grid grid-cols-7 gap-1.5 p-4">
            <USkeleton v-for="n in 14" :key="n" class="h-28" />
        </div>

        <div v-else-if="failed" class="flex flex-col items-center px-6 py-14 text-center">
            <UIcon name="i-ph-warning-duotone" class="size-8 text-red-400" />
            <p class="mt-3 text-sm font-medium text-gray-700">Couldn't load the preview</p>
            <UButton @click="fetchSlots()" :loading="loading" color="white" size="sm" class="mt-4" icon="i-ph-arrow-clockwise-bold">Retry</UButton>
        </div>

        <div v-else-if="!slots.length" class="flex flex-col items-center px-6 py-14 text-center">
            <div class="flex size-12 items-center justify-center rounded-full bg-gray-100">
                <UIcon name="i-ph-calendar-x-duotone" class="size-6 text-gray-400" />
            </div>
            <p class="mt-3 text-sm font-medium text-gray-700">No bookable slots in the next {{ days }} days</p>
            <p class="mt-1 max-w-xs text-xs text-gray-500">Add a weekly window or an extra-availability exception and the slots appear here.</p>
        </div>

        <div v-else class="overflow-x-auto p-4">
            <div class="min-w-[36rem] space-y-1.5">
                <div class="grid grid-cols-7 gap-1.5">
                    <span v-for="day in WEEKDAYS_MONDAY_FIRST" :key="day.value" class="px-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                        {{ day.short }}
                    </span>
                </div>
                <div v-for="(week, index) in weeks" :key="index" class="grid grid-cols-7 gap-1.5">
                    <template v-for="(cell, column) in week" :key="cell?.day ?? `pad-${index}-${column}`">
                        <div v-if="!cell" class="rounded-md bg-gray-50/70" />
                        <div
                            v-else
                            class="flex min-h-24 flex-col rounded-md p-1.5 ring-1 ring-inset"
                            :class="cell.slots.length ? 'bg-white ring-gray-200' : 'bg-gray-50 ring-gray-100'"
                        >
                            <div class="mb-1.5 flex px-0.5">
                                <span
                                    class="whitespace-nowrap text-xs font-semibold"
                                    :class="cell.today ? '-mx-0.5 rounded bg-brand-600 px-1 text-white' : cell.slots.length ? 'text-gray-700' : 'text-gray-400'"
                                    :title="cell.today ? 'Today' : undefined"
                                >
                                    {{ cell.date }}
                                </span>
                            </div>
                            <TransitionGroup tag="div" name="slot" class="flex flex-col gap-1">
                                <span
                                    v-for="slot in cell.slots"
                                    :key="slot.starts_at"
                                    class="rounded px-1.5 py-0.5 text-center text-xs font-medium tabular-nums ring-1 ring-inset transition-colors duration-[1500ms]"
                                    :class="fresh.has(slot.starts_at) ? 'bg-green-100 text-green-800 ring-green-300' : 'bg-brand-50 text-brand-700 ring-brand-100'"
                                >
                                    {{ dayjs(slot.starts_at).tz(timezone).format('HH:mm') }}
                                </span>
                            </TransitionGroup>
                        </div>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import type { Slot } from '~/types/telemedicine'
import { WEEKDAYS_MONDAY_FIRST, previewRange, previewWeeks } from '~/utils/telemedicineSchedule'

const props = withDefaults(defineProps<{
    doctorId: string
    timezone: string
    days?: number
    refreshKey?: number
}>(), { days: 14, refreshKey: 0 })

const client = useSupabaseClient()
const toast = useToast()

const slots = ref<Slot[]>([])
const loading = ref(false)
const loaded = ref(false)
const failed = ref(false)
const fresh = ref(new Set<string>())
const now = ref(new Date())

let request = 0
let freshTimer: ReturnType<typeof setTimeout> | undefined

const weeks = computed(() => previewWeeks(now.value, props.timezone, props.days, slots.value))

watch(() => [props.doctorId, props.timezone, props.days, props.refreshKey], () => fetchSlots(), { immediate: true })

onBeforeUnmount(() => clearTimeout(freshTimer))

async function fetchSlots() {
    const current = ++request
    loading.value = true
    now.value = new Date()
    const range = previewRange(now.value, props.timezone, props.days)

    const { data, error } = await client.rpc('telemedicine_available_slots' as never, {
        p_doctor_id: props.doctorId,
        p_from: range.from,
        p_to: range.to,
    } as never)

    if (current !== request) return
    loading.value = false

    if (error) {
        failed.value = true
        loaded.value = true
        toast.add({ title: 'Could not load the slot preview', description: error.message, icon: 'i-ph-warning-duotone', color: 'red' })
        return
    }

    const next = (data ?? []) as unknown as Slot[]
    const previous = new Set(slots.value.map((slot) => slot.starts_at))
    fresh.value = loaded.value && !failed.value ? new Set(next.map((slot) => slot.starts_at).filter((key) => !previous.has(key))) : new Set()
    slots.value = next
    failed.value = false
    loaded.value = true

    clearTimeout(freshTimer)
    if (fresh.value.size) freshTimer = setTimeout(() => (fresh.value = new Set()), 2500)
}

defineExpose({ refresh: fetchSlots })
</script>

<style scoped>
.slot-enter-active {
    transition: opacity 300ms ease, transform 300ms ease;
}
.slot-enter-from {
    opacity: 0;
    transform: scale(0.85);
}
.slot-leave-active {
    transition: opacity 200ms ease;
}
.slot-leave-to {
    opacity: 0;
}
</style>
